import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearPendingSubtaskList,
  fetchPendingSubtaskList,
} from './pendingTaskList.actions';
import { PageFilter } from '../../../../state/app';
import { AppState } from '../../../../state/configureStore';
import { PendingTaskListState } from './pendingTaskList.reducer';
import {
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  AssignmentTurnedIn as DoneIcon,
  RemoveRedEye as VisibleIcon,
  ReportProblem as OverdueIcon,
  Schedule as PendingTaskIcon,
} from '@material-ui/icons';
import { DateDisplay } from '../../../components/DateTimeDisplay';
import useStyles from './pendingTaskList.styles';
import TaskNumberLinkButton from '../../../components/TaskNumberLinkButton';
import Loading from '../../../components/AwLoading';
import { TablePageSize } from '../../../../constants';
import { AvatarGroup } from '@material-ui/lab';

const PendingTaskList = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 10,
    sort: { orderBy: 'createdAt', direction: 'asc' },
  });

  const { items: subtasks, totalItems, loading } = useSelector<
    AppState,
    PendingTaskListState
  >((state) => state.pendingTaskList);

  useEffect(() => {
    dispatch(fetchPendingSubtaskList(paging));
    return function cleanup() {
      dispatch(clearPendingSubtaskList());
    };
  }, [dispatch, paging]);

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const statCards = useMemo(() => {
    const totalCount = subtasks.length;
    const doneCount = subtasks.filter(
      (subtask) => subtask.status === 'DONE' || Boolean(subtask.completedDate),
    ).length;
    const overdueCount = subtasks.filter((subtask) => (subtask.overdue || 0) > 0).length;
    const onTimeCount = subtasks.filter(
      (subtask) =>
        (subtask.overdue || 0) <= 0 &&
        subtask.status !== 'DONE' &&
        !subtask.completedDate,
    ).length;

    return [
      {
        id: 'total',
        label: 'Total Jobs',
        value: totalCount.toLocaleString('en-US'),
        note: 'Items in current view',
        icon: PendingTaskIcon,
        accentClassName: classes.statAccentBlue,
      },
      {
        id: 'overdue',
        label: 'Overdue',
        value: overdueCount.toLocaleString('en-US'),
        note: 'Behind schedule',
        icon: OverdueIcon,
        accentClassName: classes.statAccentRed,
      },
      {
        id: 'on-time',
        label: 'On-Time',
        value: onTimeCount.toLocaleString('en-US'),
        note: 'Within due date',
        icon: VisibleIcon,
        accentClassName: classes.statAccentIndigo,
      },
      {
        id: 'done',
        label: 'Done',
        value: doneCount.toLocaleString('en-US'),
        note: 'Completed work',
        icon: DoneIcon,
        accentClassName: classes.statAccentCyan,
      },
    ];
  }, [classes, subtasks]);

  return (
    <Paper className={classes.root} elevation={2}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <PendingTaskIcon className={classes.titleIcon} />
          <Typography variant="h6" className={classes.title}>
            Pending Sub Tasks
          </Typography>
        </div>
        <Typography variant="body2" className={classes.subtitle}>
          Review outstanding subtasks, owners, and due dates in one place.
        </Typography>
      </div>

      {/* mockup ui statistics cards */}
      {/* TODO: Replace with real data */}
      {/* <div className={classes.statsSection}>
        <div className={classes.statsGrid}>
          {statCards.map((card) => {
            const Icon = card.icon;

            return (
              <div className={classes.statCard} key={card.id}>
                <div className={`${classes.statIconWrap} ${card.accentClassName}`}>
                  <Icon className={classes.statIcon} />
                </div>
                <div className={classes.statContent}>
                  <Typography variant="overline" className={classes.statLabel}>
                    {card.label}
                  </Typography>
                  <Typography variant="h5" className={classes.statValue}>
                    {card.value}
                  </Typography>
                  <Typography variant="body2" className={classes.statNote}>
                    {card.note}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      </div> */}

      <Loading loading={loading} height={800}>
        <div className={classes.tableSection}>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell className={classes.headerCell}>Task Number</TableCell>
                  <TableCell className={classes.headerCell}>Subject</TableCell>
                  <TableCell className={classes.headerCell}>Type</TableCell>
                  <TableCell className={classes.headerCell}>Requester</TableCell>
                  <TableCell className={classes.headerCell}>Assignee</TableCell>
                  <TableCell className={classes.headerCell}>Due Date</TableCell>
                  <TableCell className={classes.headerCell}>Created Date</TableCell>
                  <TableCell className={classes.headerCell}>Overdue (Days)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subtasks.map((subtask) => {
                  const assigneeUsers = subtask.smSubtaskAssignees
                    ?.map((u) => u.user.fullName)
                    .join(',  ');
                  const subject = subtask.subject || '';
                  const requesterInitial =
                    subtask.smTask?.requester?.firstName?.substring(0, 1) || '?';

                  return (
                    <TableRow key={subtask.id} className={classes.bodyRow} hover>
                      <TableCell className={classes.taskCell}>
                        <TaskNumberLinkButton
                          task={subtask.smTask}
                          smSubtaskId={subtask.id}
                        />
                      </TableCell>
                      <TableCell className={classes.subjectCell}>
                        {subject.length > 25 ? `${subject.substring(0, 25)}...` : subject}
                      </TableCell>
                      <TableCell className={classes.typeCell}>
                        {subtask.smSubtaskType ? subtask.smSubtaskType.name : ''}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            <Typography variant="body2">
                              {subtask.smTask?.requester?.fullName}
                            </Typography>
                          }
                        >
                          <Avatar className={classes.requesterAvatar}>
                            {requesterInitial}
                          </Avatar>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={<Typography variant="body2">{assigneeUsers}</Typography>}
                        >
                          <AvatarGroup max={2} className={classes.assigneeGroup}>
                            {subtask.smSubtaskAssignees?.map((assignee) => {
                              return (
                                <Avatar
                                  key={assignee.user.id || assignee.user.fullName}
                                  className={classes.assigneeAvatar}
                                >
                                  {assignee.user.firstName?.substring(0, 1)}
                                </Avatar>
                              );
                            })}
                          </AvatarGroup>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={classes.dateCell}>
                        <DateDisplay value={subtask.dueDate} />
                      </TableCell>
                      <TableCell className={classes.dateCell}>
                        <DateDisplay value={subtask.createdAt} />
                      </TableCell>
                      <TableCell>
                        <span className={classes.overdueBadge}>
                          {subtask.overdue?.toLocaleString('en-US')}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.pagination}>
            <TablePagination
              rowsPerPageOptions={[...TablePageSize]}
              component="div"
              count={totalItems}
              rowsPerPage={paging.pageSize}
              page={paging.pageNumber}
              onPageChange={handleChangePageNumber}
              onRowsPerPageChange={handleChangePageSize}
            />
          </div>
        </div>
      </Loading>
    </Paper>
  );
};

export default PendingTaskList;
